import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/brand";
import { Lightbulb, ThumbsUp, RocketLaunch, Hammer, CheckCircle, Plus } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const STATUS_META = {
  planned: {
    label: "Planned",
    icon: <Lightbulb size={18} weight="duotone" />,
    className: "bg-[#FFF7E5] border-black",
  },
  in_progress: {
    label: "In progress",
    icon: <Hammer size={18} weight="duotone" />,
    className: "bg-blue-50 border-blue-700",
  },
  shipped: {
    label: "Shipped",
    icon: <CheckCircle size={18} weight="duotone" />,
    className: "bg-emerald-50 border-emerald-700",
  },
};

export default function RoadmapPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [votedIds, setVotedIds] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/roadmap`);
      setItems(res.data.items || []);
    } catch (e) {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (title.length < 5 || description.length < 10) return;
    setBusy(true);
    try {
      await axios.post(`${API}/roadmap/suggest`, { title, description });
      toast.success("Thanks! Your suggestion is with our team. We'll review shortly.");
      setTitle("");
      setDescription("");
      setShowForm(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit suggestion");
    } finally {
      setBusy(false);
    }
  };

  const vote = async (id) => {
    if (votedIds.has(id)) return;
    try {
      const res = await axios.post(`${API}/roadmap/${id}/vote`);
      if (res.data.voted) {
        setItems((prev) => prev.map((i) => (i.id === id ? { ...i, votes: res.data.votes } : i)));
      }
      setVotedIds((prev) => new Set([...prev, id]));
    } catch (e) {
      toast.error("Could not vote — please try again.");
    }
  };

  const grouped = (status) => items.filter((i) => i.status === status);

  return (
    <div className="space-y-8" data-testid="roadmap-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="section-eyebrow flex items-center gap-2">
            <RocketLaunch size={16} weight="bold" /> Product roadmap
          </div>
          <h1 className="font-heading uppercase text-4xl mt-2">What's coming next.</h1>
          <p className="text-black/75 mt-3 leading-relaxed">
            A real look at what forecastly is working on, what's queued up next, and what's
            already shipped. This is our public build log — and it moves because you vote things
            up.
          </p>
          <p className="text-black/75 mt-3 leading-relaxed">
            Tap the thumbs-up on any planned or in-progress item to nudge it up the priority list,
            or hit "Suggest a feature" to add an idea we might not have thought of yet. Every
            suggestion goes through a quick moderation step before appearing on the board, but we
            read every single one — even the ones we can't build straight away.
          </p>
        </div>
        {user && (
          <button
            type="button"
            className="btn-primary"
            onClick={() => setShowForm((v) => !v)}
            data-testid="suggest-toggle"
          >
            <Plus size={14} weight="bold" /> Suggest a feature
          </button>
        )}
      </div>

      {showForm && (
        <div className="card" data-testid="suggest-form">
          <h2 className="font-heading uppercase text-2xl">Suggest a feature</h2>
          <p className="mt-2 text-sm text-black/65">
            Give it a clear title and a short paragraph on why it matters to you. Good ideas travel
            quickly here.
          </p>
          <form onSubmit={submit} className="mt-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold mb-1">Title</label>
              <input
                type="text"
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Multi-currency forecasting"
                required
                minLength={5}
                maxLength={140}
                data-testid="suggest-title"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Why it matters</label>
              <textarea
                className="input min-h-[120px]"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the problem you'd solve or the workflow you'd love to see."
                required
                minLength={10}
                maxLength={2000}
                data-testid="suggest-description"
              />
            </div>
            <div className="flex gap-3">
              <button type="submit" className="btn-primary" disabled={busy} data-testid="suggest-submit">
                {busy ? "Sending…" : "Send suggestion"}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <p className="text-center text-black/55">Loading roadmap…</p>
      ) : items.length === 0 ? (
        <div className="card text-center">
          <p className="text-black/65">The roadmap is being curated. Check back soon.</p>
          {user && (
            <button
              type="button"
              className="btn-primary mt-4"
              onClick={() => setShowForm(true)}
              data-testid="suggest-first"
            >
              Be the first to suggest a feature
            </button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-6">
          {["planned", "in_progress", "shipped"].map((status) => {
            const meta = STATUS_META[status];
            const bucket = grouped(status);
            return (
              <div
                key={status}
                className={`card border-2 ${meta.className}`}
                data-testid={`roadmap-column-${status}`}
              >
                <div className="flex items-center gap-2">
                  {meta.icon}
                  <h2 className="font-heading uppercase text-xl">{meta.label}</h2>
                  <span className="ml-auto text-xs text-black/55">{bucket.length}</span>
                </div>
                <div className="mt-4 space-y-3">
                  {bucket.length === 0 ? (
                    <p className="text-xs text-black/45 italic">Nothing here yet.</p>
                  ) : (
                    bucket.map((item) => (
                      <div
                        key={item.id}
                        className="p-3 bg-white rounded-xl border-[1.5px] border-black/15"
                        data-testid={`roadmap-item-${item.id}`}
                      >
                        <h3 className="font-semibold text-sm">{item.title}</h3>
                        <p className="text-xs text-black/70 mt-1 leading-relaxed">
                          {item.description}
                        </p>
                        {user && status !== "shipped" && (
                          <button
                            type="button"
                            onClick={() => vote(item.id)}
                            disabled={votedIds.has(item.id)}
                            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold hover:text-black/70 disabled:opacity-50"
                            data-testid={`roadmap-vote-${item.id}`}
                          >
                            <ThumbsUp size={12} weight={votedIds.has(item.id) ? "fill" : "bold"} />
                            {item.votes} vote{item.votes !== 1 ? "s" : ""}
                          </button>
                        )}
                        {(!user || status === "shipped") && (
                          <p className="mt-3 text-xs text-black/55">
                            {item.votes} vote{item.votes !== 1 ? "s" : ""}
                          </p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
